/**
 * 允许写入归因的落地 pathname 白名单。
 */
import Constants from '@/constants/const';

/** 从 pathname 解析语言前缀 */
const getUrlLangFromPathname = (pathname: string): string | undefined => {
  const locale = Constants.LANGUAGES.find(loc => pathname.startsWith(`/${loc}`));
  if (locale) {
    return locale;
  }

  const segmentMatch = /^\/([^/]+)/.exec(pathname);
  if (!segmentMatch) {
    return undefined;
  }

  const firstSegment = segmentMatch[1];
  if (Constants.LANGUAGE_REGEXP.test(`/${firstSegment}`)) {
    if (firstSegment === Constants.LANGUAGE_CHINESE_T_ALIAS || firstSegment.toLowerCase() === 'zh-hant') {
      return Constants.LANGUAGE_CHINESE_T;
    }
    if (Constants.LANGUAGES.includes(firstSegment)) {
      return firstSegment;
    }
  }

  return undefined;
};

/**
 * 去掉语言前缀后的 pathname。
 * 用于覆盖策略比较与 pathname 白名单匹配。
 */
export const normalizePathnameWithoutLang = (pathname: string): string => {
  const lang = getUrlLangFromPathname(pathname);
  if (!lang) {
    return pathname || '/';
  }
  const prefix = `/${lang}`;
  if (!pathname.startsWith(prefix)) {
    return pathname || '/';
  }
  const rest = pathname.slice(prefix.length);
  return rest === '' ? '/' : rest;
};

const PATH_SEGMENT = '[^/]+';
const HOME_PATH = /^\/$/;
/** 搜索页：/search 及子路径（去 lang 后） */
export const SEARCH_ENTRY_PATH_PATTERN = new RegExp(`^/search(?:/${PATH_SEGMENT}(?:/.*)?)?/?$`);
const SEARCH_PATH = SEARCH_ENTRY_PATH_PATTERN;
const CATEGORY_PATH = new RegExp(`^/category/${PATH_SEGMENT}(?:/.*)?$`);
const SHOPPING_LIST_PATH = new RegExp(`^/shopping_list(?:/${PATH_SEGMENT}(?:/.*)?)?/?$`);
/** PDP：/product/{slug}/{id}、/products/{slug}/{id}（去 lang 后） */
export const PDP_ENTRY_PATH_PATTERN = new RegExp(`^/products?/${PATH_SEGMENT}/${PATH_SEGMENT}/?$`);
const PRODUCT_PATH = PDP_ENTRY_PATH_PATTERN;
const BRAND_DETAIL_PATH = new RegExp(`^/brand/detail/${PATH_SEGMENT}/${PATH_SEGMENT}/?$`);

/** 判断 pathname（可含 lang 前缀）是否为搜索页归因落地页。 */
export const isSearchAttributedEntryPath = (pathname: string): boolean => {
  return SEARCH_ENTRY_PATH_PATTERN.test(normalizePathnameWithoutLang(pathname));
};

/** 判断完整落地 URL 是否为搜索页归因落地页。 */
export const isSearchAttributedEntryUrl = (entryUrl: string): boolean => {
  try {
    return isSearchAttributedEntryPath(new URL(entryUrl).pathname);
  } catch {
    return false;
  }
};

/** 判断 pathname（可含 lang 前缀）是否为 PDP 归因落地页。 */
export const isPdpAttributedEntryPath = (pathname: string): boolean => {
  return PDP_ENTRY_PATH_PATTERN.test(normalizePathnameWithoutLang(pathname));
};

/** 判断完整落地 URL 是否为 PDP 归因落地页。 */
export const isPdpAttributedEntryUrl = (entryUrl: string): boolean => {
  try {
    return isPdpAttributedEntryPath(new URL(entryUrl).pathname);
  } catch {
    return false;
  }
};

/**
 * 判断 pathname 是否为允许的归因落地页。
 * capture 写入的前置条件。
 */
export const isAttributedEntryPath = (pathname: string): boolean => {
  const path = normalizePathnameWithoutLang(pathname);
  return (
    HOME_PATH.test(path) ||
    SEARCH_PATH.test(path) ||
    CATEGORY_PATH.test(path) ||
    SHOPPING_LIST_PATH.test(path) ||
    PRODUCT_PATH.test(path) ||
    BRAND_DETAIL_PATH.test(path)
  );
};
